<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
  EnvelopeOpenIcon,
  CursorArrowRaysIcon,
  QrCodeIcon,
  SparklesIcon
} from '@heroicons/vue/24/outline'
import RunicAstrolabe from './RunicAstrolabe.vue'
import BrandLogo from '@/components/BrandLogo.vue'

/**
 * Event Passport — a pinned, scroll-scrubbed chapter (GSAP ScrollTrigger).
 *
 * The section pins fullscreen; scrolling scrubs a 0→1 progress that
 * drives everything: the step index (snapped so each flick lands on a
 * step), the left gauge fill, and the runic astrolabe rotating in the
 * background. Step changes swap the phone screen and the copy with
 * directional transitions, and slam a new ink stamp on the passport.
 *
 * GSAP is imported dynamically in onMounted (SSG-safe) and fully
 * cleaned up in onUnmounted via gsap.context().
 *
 * Screenshots: drop the captures from ../saas-mobile-attendee into
 * src/assets/images/homepage/passport/ then fill `screenshots` below.
 */

// import shotBadge from '@/assets/images/homepage/passport/badge.png'
// import shotActivity from '@/assets/images/homepage/passport/activity.png'
const screenshots = [null, null, null, null] // only indexes 2 and 3 are used

const STEPS = 4
const rootRef = ref(null)
const progress = ref(0)
const active = computed(() => Math.min(STEPS - 1, Math.round(progress.value * (STEPS - 1))))

const stepIcons = [EnvelopeOpenIcon, CursorArrowRaysIcon, QrCodeIcon, SparklesIcon]

// ink stamps: position/rotation/color per step, cumulative on the card
const stampStyles = [
  { '--rot': '-12deg', top: '8%', insetInlineStart: '2%', color: '#8b6ff0', radius: '9999px' },
  { '--rot': '8deg', top: '12%', insetInlineStart: '48%', color: '#d75fe8', radius: '10px' },
  { '--rot': '-7deg', top: '54%', insetInlineStart: '8%', color: '#2dd4bf', radius: '9999px' },
  { '--rot': '12deg', top: '50%', insetInlineStart: '54%', color: '#fbbf24', radius: '10px' }
]

// decorative mini-QR on the card (static pattern)
const qrPattern = [1,1,1,0,1, 1,0,0,1,0, 1,0,1,1,1, 0,1,0,0,1, 1,0,1,0,1]

const counter = computed(() => `0${active.value + 1} / 0${STEPS}`)

let gsapCtx = null
let scrollTriggerInstance = null

function jump(i) {
  const st = scrollTriggerInstance
  if (!st) return
  const target = st.start + ((st.end - st.start) * i) / (STEPS - 1)
  window.scrollTo({ top: target, behavior: 'smooth' })
}

onMounted(async () => {
  const [{ gsap }, { ScrollTrigger }] = await Promise.all([
    import('gsap'),
    import('gsap/ScrollTrigger')
  ])
  gsap.registerPlugin(ScrollTrigger)

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  gsapCtx = gsap.context(() => {
    scrollTriggerInstance = ScrollTrigger.create({
      trigger: rootRef.value,
      start: 'top top',
      end: '+=320%',
      pin: true,
      anticipatePin: 1,
      scrub: 0.5,
      // one scroll flick lands cleanly on the next step
      snap: reducedMotion
        ? false
        : {
            snapTo: 1 / (STEPS - 1),
            duration: { min: 0.25, max: 0.55 },
            ease: 'power2.inOut',
            delay: 0.05
          },
      onUpdate(self) {
        progress.value = self.progress
      }
    })
  }, rootRef.value)
})

onUnmounted(() => {
  scrollTriggerInstance = null
  gsapCtx?.revert()
})
</script>

<template>
  <section ref="rootRef" class="relative h-screen supports-[height:100svh]:h-[100svh] overflow-hidden text-white bg-[#0b0722]">
    <!-- ===== Night backdrop, continuity with the hero ===== -->
    <div
      class="absolute inset-0"
      style="
        background:
          radial-gradient(110% 80% at 78% 30%, #3b2087 0%, #221255 45%, transparent 75%),
          radial-gradient(80% 60% at 15% 85%, #1b1150 0%, transparent 60%),
          linear-gradient(180deg, #0b0722 0%, #150c3d 55%, #0b0722 100%);
      "
      aria-hidden="true"
    ></div>
    <div class="absolute left-[8%] top-[18%] w-[34rem] h-[16rem] rounded-full blur-[110px] bg-[#c445d8]/12 pointer-events-none" aria-hidden="true"></div>
    <div class="absolute right-[6%] bottom-[12%] w-[28rem] h-[14rem] rounded-full blur-[100px] bg-[#6d3bd9]/20 pointer-events-none" aria-hidden="true"></div>

    <!-- ===== The runic astrolabe — massive, subtle, scrubbed by scroll ===== -->
    <div
      class="absolute top-1/2 -translate-y-1/2 start-[30%] lg:start-[42%] w-[135vh] max-w-none opacity-[0.17] pointer-events-none"
      aria-hidden="true"
    >
      <RunicAstrolabe :progress="progress" :step="active" class="w-full h-auto" />
    </div>

    <!-- vignette keeps the copy readable over the instrument -->
    <div
      class="absolute inset-0 pointer-events-none"
      style="background: radial-gradient(115% 100% at 50% 45%, transparent 50%, rgba(6, 4, 18, 0.6) 100%)"
      aria-hidden="true"
    ></div>

    <!-- ===== Gauge / nav rail ===== -->
    <nav
      class="absolute z-20 flex items-center
             bottom-5 left-1/2 -translate-x-1/2 flex-row gap-10
             lg:bottom-auto lg:left-auto lg:translate-x-0 lg:top-1/2 lg:-translate-y-1/2 lg:flex-col lg:gap-12 lg:start-8 xl:start-14"
      :aria-label="$t('app.passport.label')"
    >
      <!-- continuous track + scrub-linked fill (horizontal < lg, vertical ≥ lg) -->
      <span class="absolute bg-white/10 rounded-full inset-x-1 top-1/2 -translate-y-1/2 h-px lg:inset-x-auto lg:inset-y-1 lg:top-auto lg:translate-y-0 lg:left-1/2 lg:-translate-x-1/2 lg:w-px lg:h-auto" aria-hidden="true"></span>
      <span
        class="lg:hidden absolute rounded-full bg-gradient-to-r from-[#ffb45c] via-[#d75fe8] to-[#8b6ff0] inset-x-1 top-1/2 -translate-y-1/2 h-[2px] origin-left rtl:origin-right"
        :style="{ transform: `translateY(-50%) scaleX(${progress})` }"
        aria-hidden="true"
      ></span>
      <span
        class="hidden lg:block absolute rounded-full bg-gradient-to-b from-[#ffb45c] via-[#d75fe8] to-[#8b6ff0] inset-y-1 left-1/2 -translate-x-1/2 w-[2px] origin-top"
        :style="{ transform: `translateX(-50%) scaleY(${progress})` }"
        aria-hidden="true"
      ></span>

      <button
        v-for="(icon, i) in stepIcons"
        :key="i"
        @click="jump(i)"
        class="group relative flex items-center justify-center w-12 h-12 rounded-full border transition-all duration-400"
        :class="i === active
          ? 'border-transparent bg-gradient-to-br from-[#d75fe8] to-[#7c4fe0] shadow-lg shadow-[#a03fd8]/40 scale-110'
          : i < active
            ? 'border-white/25 bg-[#241a4f] text-white/80 hover:bg-[#2b1f5e]'
            : 'border-white/15 bg-[#150c3d] text-white/40 hover:text-white/70 hover:border-white/30'"
        :aria-current="i === active ? 'step' : undefined"
        :aria-label="$t(`app.passport.steps[${i}].nav`)"
      >
        <component :is="icon" class="w-5 h-5" :class="i === active ? 'text-white' : ''" />
        <span
          v-if="i === active"
          class="absolute inset-0 rounded-full ring-2 ring-[#d75fe8]/40 animate-ping-slow"
          aria-hidden="true"
        ></span>
        <span
          class="pointer-events-none absolute whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300
                 -top-7 lg:top-auto lg:start-16"
          :class="i === active ? 'opacity-90 text-white' : 'opacity-0 lg:group-hover:opacity-60 text-white/70'"
        >
          {{ $t(`app.passport.steps[${i}].nav`) }}
        </span>
      </button>
    </nav>

    <!-- ===== Content ===== -->
    <div class="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-10 lg:ps-40 xl:ps-48 lg:pe-8 grid lg:grid-cols-2 items-center gap-8">
      <!-- Left: chapter header + step copy -->
      <div class="pt-16 pb-40 lg:py-0">
        <p class="text-xs font-mono text-[#d7a6ff] tracking-widest uppercase mb-3">{{ $t('app.passport.label') }}</p>
        <h2 class="text-3xl md:text-4xl font-semibold text-white/90 mb-8 lg:mb-14 max-w-md">{{ $t('app.passport.title') }}</h2>

        <p class="font-mono text-sm text-white/40 tabular-nums mb-4">{{ counter }}</p>

        <transition
          mode="out-in"
          enter-active-class="transition duration-500 ease-out"
          enter-from-class="opacity-0 translate-y-6"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-250 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0 -translate-y-4"
        >
          <div :key="active" class="max-w-lg">
            <h3 class="text-3xl md:text-5xl font-semibold leading-[1.12] mb-5">
              {{ $t(`app.passport.steps[${active}].title`) }}
            </h3>
            <p class="text-base md:text-lg text-white/60 leading-relaxed">
              {{ $t(`app.passport.steps[${active}].desc`) }}
            </p>
          </div>
        </transition>
      </div>

      <!-- Right: phone + passport -->
      <div class="hidden sm:flex justify-center lg:justify-end items-center relative">
        <div class="relative me-0 lg:me-24">
          <!-- Phone -->
          <div class="relative rounded-[2.6rem] bg-slate-900 p-2 shadow-2xl shadow-black/50 ring-1 ring-white/10">
            <div class="relative rounded-[2.1rem] overflow-hidden bg-slate-950 aspect-[9/19]" style="height: min(62svh, 560px)">
              <div class="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-slate-900 z-20" aria-hidden="true"></div>

              <transition
                mode="out-in"
                enter-active-class="transition duration-500 ease-out"
                enter-from-class="opacity-0 translate-y-8 scale-[0.97]"
                enter-to-class="opacity-100 translate-y-0 scale-100"
                leave-active-class="transition duration-250 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0 -translate-y-6 scale-[0.98]"
              >
                <!-- STEP 1 — invitation message (hand-built) -->
                <div v-if="active === 0" key="s0" class="absolute inset-0 bg-gradient-to-b from-[#131024] to-[#0b0722] flex flex-col">
                  <div class="pt-12 pb-3 px-4 border-b border-white/5 flex items-center gap-3">
                    <span class="w-8 h-8 rounded-full bg-gradient-to-br from-[#d75fe8] to-[#7c4fe0] flex items-center justify-center text-[10px] font-bold">B</span>
                    <span>
                      <span class="block text-[12px] font-semibold text-white/90">{{ $t('app.passport.vignette.sender') }}</span>
                      <span class="block text-[10px] text-white/40">{{ $t('app.passport.vignette.preview') }}</span>
                    </span>
                  </div>
                  <div class="flex-1 px-3 py-4">
                    <div class="max-w-[85%] rounded-2xl rounded-ss-sm bg-white/[0.07] border border-white/10 p-3 space-y-3">
                      <div class="rounded-lg h-16 bg-gradient-to-br from-[#3b2087] to-[#c445d8]/60 flex items-end p-2">
                        <span class="text-[10px] font-mono text-white/80">{{ $t('app.passport.vignette.preview') }}</span>
                      </div>
                      <p class="text-[12px] text-white/80 leading-snug">{{ $t('app.passport.vignette.message') }}</p>
                      <span class="block text-center rounded-lg bg-gradient-to-r from-[#d75fe8] to-[#7c4fe0] py-2 text-[12px] font-semibold">
                        {{ $t('app.passport.vignette.cta') }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- STEP 2 — the tap, app opens (hand-built) -->
                <div v-else-if="active === 1" key="s1" class="absolute inset-0 bg-gradient-to-b from-[#1b1150] via-[#2a1763] to-[#0b0722] flex flex-col items-center justify-center gap-6">
                  <BrandLogo :size="26" variant="white" class="text-white" />
                  <span class="w-28 h-1 rounded-full bg-white/10 overflow-hidden">
                    <span class="block h-full w-1/2 rounded-full bg-gradient-to-r from-[#d75fe8] to-[#8b6ff0] animate-loading-bar"></span>
                  </span>
                  <span class="text-[11px] font-mono text-white/50">{{ $t('app.passport.vignette.opening') }}</span>
                </div>

                <!-- STEPS 3 & 4 — real captures (placeholder until provided) -->
                <div v-else :key="`s${active}`" class="absolute inset-0">
                  <img
                    v-if="screenshots[active]"
                    :src="screenshots[active]"
                    class="w-full h-full object-cover"
                    :alt="$t(`app.passport.steps[${active}].screen`)"
                  />
                  <div
                    v-else
                    class="w-full h-full p-6 flex flex-col items-center justify-center text-center gap-4"
                    :class="active === 2
                      ? 'bg-gradient-to-br from-[#1b1150] via-[#2f1b70] to-[#4c2ba8]'
                      : 'bg-gradient-to-br from-[#3b2087] via-[#241454] to-[#0b0722]'"
                  >
                    <component :is="stepIcons[active]" class="w-10 h-10 text-white/80" />
                    <p class="text-sm text-white/90 font-medium leading-snug">
                      {{ $t(`app.passport.steps[${active}].screen`) }}
                    </p>
                    <div class="mt-1 border border-dashed border-white/30 rounded-lg px-3 py-1.5 text-[11px] font-mono text-white/50">
                      {{ $t('app.passport.card.note') }}
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Passport card -->
          <div
            class="hidden md:block absolute -bottom-8 -start-44 rotate-[-7deg] w-[280px] rounded-2xl bg-[#f7f2e7] text-[#241454] shadow-2xl shadow-black/40 ring-1 ring-black/10 p-5 overflow-hidden"
          >
            <div
              class="absolute inset-y-0 start-0 w-2 opacity-15"
              style="background-image: radial-gradient(circle at 50% 8px, #241454 2.2px, transparent 2.5px); background-size: 8px 16px"
              aria-hidden="true"
            ></div>

            <div class="flex items-baseline justify-between mb-3 ps-2">
              <span class="font-mono text-[11px] tracking-[0.25em] font-bold">BIFROST</span>
              <span class="font-mono text-[9px] tracking-[0.2em] text-[#6d4fd0] font-bold">EVENT PASSPORT</span>
            </div>

            <div class="border-t border-dashed border-[#241454]/25 pt-3 ps-2 flex items-start justify-between gap-4">
              <div class="space-y-3 flex-1">
                <div>
                  <span class="block text-[9px] font-mono uppercase tracking-widest opacity-50">{{ $t('app.passport.card.holder') }}</span>
                  <span class="mt-1 block h-2 w-24 rounded bg-[#241454]/15" aria-hidden="true"></span>
                </div>
                <div>
                  <span class="block text-[9px] font-mono uppercase tracking-widest opacity-50">{{ $t('app.passport.card.event') }}</span>
                  <span class="mt-1 block h-2 w-32 rounded bg-[#241454]/15" aria-hidden="true"></span>
                </div>
              </div>
              <div class="grid grid-cols-5 gap-[3px] p-1.5 rounded border border-[#241454]/20" aria-hidden="true">
                <span
                  v-for="(bit, b) in qrPattern"
                  :key="b"
                  class="w-[5px] h-[5px] rounded-[1px]"
                  :class="bit ? 'bg-[#241454]/80' : 'bg-transparent'"
                ></span>
              </div>
            </div>

            <div class="relative h-28 mt-4 ms-2 border-t border-dashed border-[#241454]/25">
              <div
                v-for="(s, i) in stampStyles.slice(0, active + 1)"
                :key="i"
                class="stamp absolute border-[2.5px] px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] whitespace-nowrap"
                :style="{
                  '--rot': s['--rot'],
                  top: s.top,
                  insetInlineStart: s.insetInlineStart,
                  color: s.color,
                  borderColor: s.color,
                  borderRadius: s.radius,
                  filter: 'saturate(0.8) brightness(0.85)'
                }"
              >
                {{ $t(`app.passport.steps[${i}].stamp`) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.stamp {
  transform: rotate(var(--rot));
  animation: stamp-in 0.45s cubic-bezier(0.2, 1.4, 0.4, 1) both;
  mask-image: radial-gradient(120% 120% at 45% 40%, #000 55%, rgba(0, 0, 0, 0.55) 100%);
}

@keyframes stamp-in {
  0% {
    transform: scale(2.1) rotate(var(--rot));
    opacity: 0;
  }
  60% {
    transform: scale(0.94) rotate(var(--rot));
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(var(--rot));
    opacity: 1;
  }
}

.animate-ping-slow {
  animation: ping-slow 2.2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

@keyframes ping-slow {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  75%,
  100% {
    transform: scale(1.45);
    opacity: 0;
  }
}

.animate-loading-bar {
  animation: loading-bar 1.4s ease-in-out infinite;
}

@keyframes loading-bar {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
}

[dir='rtl'] .animate-loading-bar {
  animation-name: loading-bar-rtl;
}

@keyframes loading-bar-rtl {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-200%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .stamp,
  .animate-ping-slow,
  .animate-loading-bar {
    animation: none;
  }
}
</style>
